import { TICK_RATE, } from "./types.js";
export const DEFAULT_GAME_CONFIG = Object.freeze({
    table: Object.freeze({
        width: 1_000,
        height: 1_600,
        wallInset: 40,
        cornerRadius: 140,
        goalWidth: 360,
        goalDepth: 100,
        postRadius: 26,
    }),
    puck: Object.freeze({
        radius: 32,
        mass: 1,
        maxSpeed: 2_400,
        frictionPerSecond: 0.12,
        wallRestitution: 0.96,
        postRestitution: 0.94,
        malletRestitution: 0.92,
        stuckSpeed: 6,
        stuckTicks: TICK_RATE * 8,
    }),
    mallet: Object.freeze({
        radius: 72,
        mass: 4,
        // One authoritative tick can cover the full legal player half. Pointer
        // control therefore feels direct while swept collision detection still
        // accounts for the entire mallet path on the server.
        maxSpeed: 75_000,
        maxAcceleration: 4_500_000,
        centerLineGap: 8,
    }),
    rules: Object.freeze({
        mode: "classic",
        pointsToWin: 7,
        durationTicks: null,
        initialCountdownTicks: TICK_RATE * 3,
        restartCountdownTicks: TICK_RATE * 2,
        goalPauseTicks: TICK_RATE,
    }),
    physics: Object.freeze({
        tickRate: TICK_RATE,
        maxCollisionsPerTick: 16,
        collisionEpsilon: 1e-7,
        statePrecision: 1e-6,
    }),
});
export function resolveGameConfig(overrides = {}) {
    const config = {
        table: { ...DEFAULT_GAME_CONFIG.table, ...overrides.table },
        puck: { ...DEFAULT_GAME_CONFIG.puck, ...overrides.puck },
        mallet: { ...DEFAULT_GAME_CONFIG.mallet, ...overrides.mallet },
        rules: { ...DEFAULT_GAME_CONFIG.rules, ...overrides.rules },
        physics: { ...DEFAULT_GAME_CONFIG.physics, ...overrides.physics },
    };
    assertValidConfig(config);
    return config;
}
function positive(value, name) {
    if (!Number.isFinite(value) || value <= 0) {
        throw new RangeError(`${name} must be a finite positive number`);
    }
}
function nonNegativeInteger(value, name) {
    if (!Number.isSafeInteger(value) || value < 0) {
        throw new RangeError(`${name} must be a non-negative safe integer`);
    }
}
export function assertValidConfig(config) {
    positive(config.table.width, "table.width");
    positive(config.table.height, "table.height");
    positive(config.table.wallInset, "table.wallInset");
    positive(config.table.cornerRadius, "table.cornerRadius");
    positive(config.table.goalWidth, "table.goalWidth");
    positive(config.table.goalDepth, "table.goalDepth");
    positive(config.table.postRadius, "table.postRadius");
    positive(config.puck.radius, "puck.radius");
    positive(config.puck.mass, "puck.mass");
    positive(config.puck.maxSpeed, "puck.maxSpeed");
    positive(config.mallet.radius, "mallet.radius");
    positive(config.mallet.mass, "mallet.mass");
    positive(config.mallet.maxSpeed, "mallet.maxSpeed");
    positive(config.mallet.maxAcceleration, "mallet.maxAcceleration");
    nonNegativeInteger(config.rules.initialCountdownTicks, "rules.initialCountdownTicks");
    nonNegativeInteger(config.rules.restartCountdownTicks, "rules.restartCountdownTicks");
    nonNegativeInteger(config.rules.goalPauseTicks, "rules.goalPauseTicks");
    nonNegativeInteger(config.puck.stuckTicks, "puck.stuckTicks");
    positive(config.physics.maxCollisionsPerTick, "physics.maxCollisionsPerTick");
    if (!Number.isSafeInteger(config.physics.maxCollisionsPerTick)) {
        throw new RangeError("physics.maxCollisionsPerTick must be a positive safe integer");
    }
    positive(config.physics.statePrecision, "physics.statePrecision");
    if (config.physics.tickRate !== TICK_RATE) {
        throw new RangeError(`physics.tickRate is fixed at ${TICK_RATE}`);
    }
    if (config.table.goalWidth >= config.table.width - config.table.wallInset * 2) {
        throw new RangeError("table.goalWidth must leave solid end-wall sections");
    }
    if (config.table.wallInset * 2 >= Math.min(config.table.width, config.table.height)) {
        throw new RangeError("table.wallInset leaves no playable surface");
    }
    if (config.table.cornerRadius * 2 >= Math.min(config.table.width, config.table.height)) {
        throw new RangeError("table.cornerRadius is too large for the table");
    }
    if (config.table.cornerRadius <= Math.max(config.puck.radius, config.mallet.radius)) {
        throw new RangeError("table.cornerRadius must exceed both body radii");
    }
    if (config.table.goalWidth <= config.puck.radius * 2) {
        throw new RangeError("table.goalWidth must be wider than the puck");
    }
    if (config.mallet.radius * 2 + config.mallet.centerLineGap >= config.table.height / 2) {
        throw new RangeError("mallet is too large for a player half");
    }
    if (config.rules.mode === "timed") {
        if (config.rules.durationTicks === null) {
            throw new RangeError("timed mode requires rules.durationTicks");
        }
        positive(config.rules.durationTicks, "rules.durationTicks");
        if (!Number.isSafeInteger(config.rules.durationTicks)) {
            throw new RangeError("rules.durationTicks must be a positive safe integer");
        }
    }
    if (!Number.isSafeInteger(config.rules.pointsToWin) || config.rules.pointsToWin <= 0) {
        throw new RangeError("rules.pointsToWin must be a positive safe integer");
    }
    if (config.puck.frictionPerSecond < 0 || !Number.isFinite(config.puck.frictionPerSecond)) {
        throw new RangeError("puck.frictionPerSecond must be finite and non-negative");
    }
    if (config.puck.stuckSpeed < 0 || !Number.isFinite(config.puck.stuckSpeed)) {
        throw new RangeError("puck.stuckSpeed must be finite and non-negative");
    }
    for (const [name, restitution] of [
        ["puck.wallRestitution", config.puck.wallRestitution],
        ["puck.postRestitution", config.puck.postRestitution],
        ["puck.malletRestitution", config.puck.malletRestitution],
    ]) {
        if (!Number.isFinite(restitution) || restitution < 0 || restitution > 1.25) {
            throw new RangeError(`${name} must be between 0 and 1.25`);
        }
    }
}
//# sourceMappingURL=config.js.map